import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { DEFAULT_AGENT_VOICE } from '../constants/agentVoices';
import {
  DEFAULT_AGENT_LANGUAGE_CODE,
  normalizeAgentLanguageCode,
} from '../constants/agentConfig';

export const DEFAULT_PHONE_NUMBER_LABEL = 'Գլխավոր համար';

const trimString = (value) => (typeof value === 'string' ? value.trim() : '');

const toDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }

  return null;
};

const getTimeValue = (value) => {
  const dateValue = toDateValue(value);

  if (!dateValue || Number.isNaN(dateValue.getTime())) {
    return 0;
  }

  return dateValue.getTime();
};

const normalizeUserPhoneNumbers = (userData = null) => {
  if (Array.isArray(userData?.agentPhoneNumbers)) {
    return userData.agentPhoneNumbers;
  }

  return [];
};

const normalizeAgentPayload = (data) => ({
  name: trimString(data?.name),
  behavior: trimString(data?.behavior || data?.description),
  greeting: trimString(data?.greeting),
  knowledgeBase: trimString(data?.knowledgeBase),
  voice: trimString(data?.voice) || DEFAULT_AGENT_VOICE,
  languageCode: normalizeAgentLanguageCode(data?.languageCode),
  phoneNumberId: trimString(data?.phoneNumberId),
});

const createPhoneNumberSnapshot = (phoneNumberData) => ({
  phoneNumber: phoneNumberData.phoneNumber,
  label: phoneNumberData.label || DEFAULT_PHONE_NUMBER_LABEL,
  status: phoneNumberData.status || 'active',
});

const mapDocs = (snapshot) => snapshot.docs.map((snapshotDoc) => {
  const data = snapshotDoc.data() || {};

  return {
    id: snapshotDoc.id,
    ...data,
    behavior: trimString(data.behavior || data.description),
    languageCode: normalizeAgentLanguageCode(data.languageCode),
  };
});

const assertPhoneNumberAvailable = ({ agents, phoneNumberId, currentAgentId = '' }) => {
  const conflictingAgent = agents.find(
    (agent) => agent.phoneNumberId === phoneNumberId && agent.id !== currentAgentId
  );

  if (conflictingAgent) {
    throw new Error('Այս համարը արդեն կցված է մեկ այլ ագենտի։');
  }
};

export const formatFirestoreDate = (value) => {
  const dateValue = toDateValue(value);

  if (!dateValue || Number.isNaN(dateValue.getTime())) {
    return '—';
  }

  return new Intl.DateTimeFormat('hy-AM', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dateValue);
};

export const sortByMostRecent = (items) => (
  [...items].sort(
    (left, right) => getTimeValue(right.updatedAt || right.createdAt) - getTimeValue(left.updatedAt || left.createdAt)
  )
);

export const isPhoneNumberSelectableForAgent = (phoneNumber, agentId = '') => (
  !phoneNumber?.assignedAgentId || phoneNumber.assignedAgentId === agentId
);

export const fetchUserAgents = async (userId) => {
  const agentsQuery = query(collection(db, 'agents'), where('userId', '==', userId));
  const snapshot = await getDocs(agentsQuery);
  return sortByMostRecent(mapDocs(snapshot));
};

export const getUserAgentPhoneNumbers = ({ userData, agents = [] }) => {
  const phoneNumbers = normalizeUserPhoneNumbers(userData);

  return phoneNumbers.map((phoneNumber) => {
    const assignedAgent = agents.find((agent) => agent.phoneNumberId === phoneNumber.id);

    return {
      ...phoneNumber,
      assignedAgentId: assignedAgent?.id || null,
      assignedAgentName: assignedAgent?.name || null,
    };
  });
};

export const createAgentForUser = async ({ userId, userData, data, agents = null }) => {
  if (!userId) {
    throw new Error('Օգտատերը նույնականացված չէ։');
  }

  const payload = normalizeAgentPayload(data);

  if (!payload.phoneNumberId) {
    throw new Error('Ընտրեք հեռախոսահամար։');
  }

  const availablePhoneNumbers = normalizeUserPhoneNumbers(userData);
  const selectedPhoneNumber = availablePhoneNumbers.find((phoneNumber) => phoneNumber.id === payload.phoneNumberId);

  if (!selectedPhoneNumber) {
    throw new Error('Ընտրված համարը հասանելի չէ։');
  }

  const existingAgents = agents || await fetchUserAgents(userId);
  assertPhoneNumberAvailable({ agents: existingAgents, phoneNumberId: payload.phoneNumberId });

  const now = new Date();
  const phoneNumberSnapshot = createPhoneNumberSnapshot(selectedPhoneNumber);
  const agentRef = await addDoc(collection(db, 'agents'), {
    userId,
    name: payload.name,
    behavior: payload.behavior,
    description: payload.behavior,
    greeting: payload.greeting,
    knowledgeBase: payload.knowledgeBase,
    voice: payload.voice,
    languageCode: payload.languageCode,
    phoneNumberId: payload.phoneNumberId,
    phoneNumberSnapshot,
    status: 'active',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return {
    id: agentRef.id,
    userId,
    name: payload.name,
    behavior: payload.behavior,
    description: payload.behavior,
    greeting: payload.greeting,
    knowledgeBase: payload.knowledgeBase,
    voice: payload.voice,
    languageCode: payload.languageCode || DEFAULT_AGENT_LANGUAGE_CODE,
    phoneNumberId: payload.phoneNumberId,
    phoneNumberSnapshot,
    status: 'active',
    createdAt: now,
    updatedAt: now,
  };
};

export const updateAgentForUser = async ({ userId, userData, agentId, data, agents = null }) => {
  if (!userId || !agentId) {
    throw new Error('Ագենտը չի գտնվել։');
  }

  const payload = normalizeAgentPayload(data);

  if (!payload.phoneNumberId) {
    throw new Error('Ընտրեք հեռախոսահամար։');
  }

  const availablePhoneNumbers = normalizeUserPhoneNumbers(userData);
  const selectedPhoneNumber = availablePhoneNumbers.find((phoneNumber) => phoneNumber.id === payload.phoneNumberId);

  if (!selectedPhoneNumber) {
    throw new Error('Ընտրված համարը հասանելի չէ։');
  }

  const existingAgents = agents || await fetchUserAgents(userId);
  assertPhoneNumberAvailable({
    agents: existingAgents,
    phoneNumberId: payload.phoneNumberId,
    currentAgentId: agentId,
  });

  const phoneNumberSnapshot = createPhoneNumberSnapshot(selectedPhoneNumber);

  await updateDoc(doc(db, 'agents', agentId), {
    name: payload.name,
    behavior: payload.behavior,
    description: payload.behavior,
    greeting: payload.greeting,
    knowledgeBase: payload.knowledgeBase,
    voice: payload.voice,
    languageCode: payload.languageCode,
    phoneNumberId: payload.phoneNumberId,
    phoneNumberSnapshot,
    updatedAt: serverTimestamp(),
  });

  return {
    id: agentId,
    userId,
    name: payload.name,
    behavior: payload.behavior,
    description: payload.behavior,
    greeting: payload.greeting,
    knowledgeBase: payload.knowledgeBase,
    voice: payload.voice,
    languageCode: payload.languageCode || DEFAULT_AGENT_LANGUAGE_CODE,
    phoneNumberId: payload.phoneNumberId,
    phoneNumberSnapshot,
    status: 'active',
    updatedAt: new Date(),
  };
};

export const deleteAgentForUser = async ({ agentId }) => {
  if (!agentId) {
    throw new Error('Ագենտը չի գտնվել։');
  }

  await deleteDoc(doc(db, 'agents', agentId));
};
